import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Trash2 } from 'lucide-react';
import axios from 'axios';
import CustomerSelectorField from './CustomerSelectorField';
import { useAccountCustomers } from '../core/useAccountCustomers';
import {
  createAccountPayment,
  updateAccountPayment,
  getAccountPaymentById,
  getAccountInvoices,
  uploadMenuFiles,
  deleteMenuFile,
} from '../core/_requests';
import { setupAxios } from '../../auth';
import SearchableSelect from '../../../shared/components/SearchableSelect'
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

type UploadedFile = {
  originalName: string;
  fileName: string;
  size: number;
  mimeType?: string;
  path: string;
  url?: string;
};

export default function AccountsNewPaymentReceived() {
  const navigate = useNavigate();
  const { id } = useParams();
  const paymentId = id ? Number(id) : null;
  const isEditing = paymentId !== null && !Number.isNaN(paymentId);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);

  const customersHook = useAccountCustomers();

  // Form State
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [bankCharges, setBankCharges] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentNumber, setPaymentNumber] = useState(`PAY-${String(Date.now()).slice(-5)}`);
  const [paymentMode, setPaymentMode] = useState('');
  const [depositTo, setDepositTo] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const buildUploadedFiles = (raw: any): UploadedFile[] => {
    const entries = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    return entries
      .map((entry) => (typeof entry === 'string' ? entry.replace(/\\/g, '/').trim() : ''))
      .filter(Boolean)
      .map((entry) => {
        const fileName = entry.split('/').pop() || entry;
        let url = entry;
        if (!/^https?:\/\//i.test(entry)) {
          const basePath = entry.startsWith('/') ? entry : `/${entry}`;
          const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
          url = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return {
          originalName: fileName,
          fileName,
          size: 0,
          mimeType: '',
          path: entry,
          url,
        } as UploadedFile;
      });
  };

  const [loadingPayment, setLoadingPayment] = useState(false);
  const [allInvoices, setAllInvoices] = useState<any[]>([]);
  const [openInvoices, setOpenInvoices] = useState<any[]>([]);

  // Fetch all invoices once
  useEffect(() => {
    getAccountInvoices().then(data => setAllInvoices(data || [])).catch(console.error);
  }, []);

  // Filter invoices for the selected customer
  useEffect(() => {
    if (selectedCustomerId && allInvoices.length > 0) {
      const customerInvoices = allInvoices.filter(
        inv => String(inv.customerId) === String(selectedCustomerId) && (
          (inv.status !== 'Closed' && inv.status !== 'Paid') || 
          (isEditing && invoiceNumber && inv.invoiceNumber === invoiceNumber)
        )
      );
      setOpenInvoices(customerInvoices);
    } else {
      setOpenInvoices([]);
    }
  }, [selectedCustomerId, allInvoices, isEditing, invoiceNumber]);

  useEffect(() => {
    if (!isEditing || paymentId == null) return;
    let isActive = true;
    setLoadingPayment(true);
    getAccountPaymentById(paymentId)
      .then((data) => {
        if (!isActive) return;
        setSelectedCustomerId(data.customerId ? String(data.customerId) : '');
        setSelectedCustomerName(data.customerName || '');
        setAmountReceived(data.amount ? String(data.amount) : '');
        setPaymentDate(data.paymentDate || '');
        setPaymentNumber(data.paymentNumber || `PAY-${String(Date.now()).slice(-5)}`);
        setPaymentMode(data.paymentMode || '');
        setDepositTo(data.depositTo || '');
        setReferenceNumber(data.referenceNumber || '');
        setInvoiceNumber(data.invoiceNumber || '');
        setNotes(data.notes || '');

        const filePathValue = data.filePath ?? data.filePaths ?? data.file_path ?? data.filepath ?? '';
        const existingFiles = buildUploadedFiles(filePathValue);
        setUploadedFiles(existingFiles);
      })
      .catch(console.error)
      .finally(() => {
        if (isActive) setLoadingPayment(false);
      });
    return () => { isActive = false; };
  }, [isEditing, paymentId]);

  const handleSave = async (status = 'Recorded') => {
    if (!paymentNumber.trim()) {
      alert("Please enter a payment number");
      return;
    }
    if (!amountReceived || Number(amountReceived) <= 0) {
      alert("Please enter a valid amount received");
      return;
    }
    setSaving(true);
    const uploadedPaths = uploadedFiles.map((file) => file.path).filter(Boolean);
    try {
      const invMatch = openInvoices.find(i => i.invoiceNumber === invoiceNumber);

      const payload = {
        customerId: selectedCustomerId ? Number(selectedCustomerId) : null,
        customerName: selectedCustomerName,
        paymentNumber: paymentNumber,
        referenceNumber: referenceNumber,
        paymentDate: paymentDate || null,
        paymentMode: paymentMode,
        invoiceId: invMatch ? Number(invMatch.id) : null,
        invoiceNumber: invoiceNumber,
        depositTo: depositTo,
        amount: Number(amountReceived) || 0,
        notes: notes,
        filePath: uploadedPaths.length ? uploadedPaths.join(',') : null,
      };

      if (isEditing && paymentId) {
        await updateAccountPayment(paymentId, payload);
      } else {
        await createAccountPayment(payload);
      }
      navigate('/accounts/sales/payments-received');
    } catch (err: any) {
      console.error(err);
      alert('Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  const triggerDocumentSelect = () => {
    documentInputRef.current?.click();
  };

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [] as File[]);
    if (!files.length) {
      return;
    }

    setUploadingFiles(true);
    setUploadError(null);

    try {
      const uploaded = await uploadMenuFiles('payments', files);
      const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
      const formattedUploads = (uploaded as UploadedFile[]).map((f) => {
        let fileUrl = f.url || f.path;
        if (!/^https?:\/\//i.test(fileUrl)) {
          const basePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return { ...f, url: fileUrl };
      });
      setUploadedFiles((prev) => [...prev, ...formattedUploads]);
    } catch (err) {
      const message = 'Upload failed.';
      setUploadError(message);
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      if (file.fileName) {
        await deleteMenuFile('payments', file.fileName);
      }
      setUploadedFiles((prev) => prev.filter((f) => f.path !== file.path));
    } catch (err) {
      const message = 'Delete failed.';
      setUploadError(message);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";

  if (loadingPayment) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading Payment...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">

              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts')}>ACCOUNTS</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts/sales')}>SALES</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-blue-600">PAYMENTS RECEIVED</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">
                    {isEditing ? `Edit Payment: ${paymentNumber}` : 'New Payment Received'}
                  </h1>
                  <p className="text-sm font-medium text-slate-500 mt-1">Record or edit a payment received</p>
                </div>
              </div>

              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8">

                {/* Primary Details Section */}
                <div className="mb-8 max-w-4xl space-y-6">
                  {/* Customer Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <CustomerSelectorField
                        value={selectedCustomerId}
                        onChange={(id, name) => { setSelectedCustomerId(id); setSelectedCustomerName(name); }}
                        customersHook={customersHook}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Invoice# */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Invoice#</label>
                    </div>
                    <div className="flex-1">
                      {selectedCustomerId ? (
                        <SearchableSelect
                          value={invoiceNumber}
                          onChange={(e) => {
                            const invNum = e.target.value;
                            setInvoiceNumber(invNum);
                            const inv = openInvoices.find(i => i.invoiceNumber === invNum);
                            if (inv) {
                              setAmountReceived(String(inv.balanceDue ?? inv.total ?? 0));
                            } else if (!invNum) {
                              setAmountReceived('');
                            }
                          }}
                          className={`${inputClass} ${isEditing ? 'bg-slate-50 opacity-70 cursor-not-allowed' : ''}`}
                          disabled={isEditing}
                        >
                          <option value="">Select Invoice</option>
                          {openInvoices.map(inv => (
                            <option key={inv.id} value={inv.invoiceNumber}>
                              {inv.invoiceNumber} (Due: ${inv.balanceDue ?? inv.total ?? 0})
                            </option>
                          ))}
                        </SearchableSelect>
                      ) : (
                        <input
                          type="text"
                          value={invoiceNumber}
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          placeholder="Select customer first..."
                          className={inputClass}
                          disabled
                        />
                      )}
                      {selectedCustomerId && openInvoices.length === 0 && (
                        <p className="text-[11px] text-amber-600 mt-1 font-medium">No open invoices found for this customer.</p>
                      )}
                    </div>
                  </div>

                  {/* Amount Received */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Amount Received</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="number" step="0.01" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder="0.00" className={inputClass} />
                    </div>
                  </div>

                  {/* Bank Charges */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Bank Charges</label>
                    </div>
                    <div className="flex-1">
                      <input type="number" step="0.01" value={bankCharges} onChange={(e) => setBankCharges(e.target.value)} placeholder="0.00" className={inputClass} />
                    </div>
                  </div>

                  {/* Payment Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Payment Date</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Payment# */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Payment#</label>
                    </div>
                    <div className="flex-1">
                      <input type="text" value={paymentNumber} onChange={(e) => setPaymentNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Payment Mode */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Payment Mode</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className={inputClass}>
                        <option value="">Select mode</option>
                        <option value="Cash">Cash</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Credit Card">Credit Card</option>
                        <option value="UPI">UPI</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Deposit To */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Deposit To</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={depositTo} onChange={(e) => setDepositTo(e.target.value)} className={inputClass}>
                        <option value="">Select account</option>
                        <option value="Petty Cash">Petty Cash</option>
                        <option value="Undeposited Funds">Undeposited Funds</option>
                        <option value="Bank Account">Bank Account</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Reference# */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Reference#</label>
                    </div>
                    <div className="flex-1">
                      <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Notes</label>
                    </div>
                    <div className="flex-1">
                      <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes" className={`${inputClass} resize-none`}></textarea>
                    </div>
                  </div>

                  {/* Attach Files */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Attach File(s)</label>
                    </div>
                    <div className="flex-1">
                      <input ref={documentInputRef} type="file" multiple hidden onChange={handleDocumentChange} />
                      <button
                        type="button"
                        onClick={triggerDocumentSelect}
                        disabled={uploadingFiles}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4 text-slate-400" /> {uploadingFiles ? 'Uploading...' : 'Upload File'}
                      </button>
                      <p className="text-[11px] text-slate-400 mt-2 font-medium">Max 5 files, 10MB each</p>
                      {uploadError && <p className="text-[11px] text-red-500 mt-1 font-medium">{uploadError}</p>}
                      {uploadedFiles.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {uploadedFiles.map((file) => (
                            <li key={`${file.fileName}-${file.path || ''}`} className="flex items-center gap-2 group">
                              <a href={file.url || file.path} target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                {file.originalName || file.fileName || file.path}
                              </a>
                              <button
                                type="button"
                                onClick={() => handleDeleteFile(file)}
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete file"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-8 mt-8 border-t border-slate-100 flex items-center gap-3">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:shadow transition-all hover:bg-slate-50 disabled:opacity-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

